import type { ReactNode } from 'react';

/** Animated placeholder block. Every loading state on this site is built of these. */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`relative overflow-hidden rounded-brand-sm bg-brand-elevate ${className}`}
    >
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-brand-soft to-transparent" />
    </div>
  );
}

/**
 * A loading region.
 *
 * `aria-busy` plus a visually hidden label, rather than a spinner with no name:
 * a screen reader otherwise announces nothing at all while the page is empty,
 * which is indistinguishable from a page that failed.
 */
export function LoadingBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <LoadingBlock label="Loading…">
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
          <div key={i} className="e67-card px-5 py-5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-3 h-5 w-2/3" />
            <Skeleton className="mt-2.5 h-3.5 w-1/3" />
          </div>
        ))}
      </div>
    </LoadingBlock>
  );
}

export function FormSkeleton() {
  return (
    <LoadingBlock label="Loading the form…">
      <div className="e67-card px-6 py-8 sm:px-9 sm:py-10">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-8 w-1/2" />
        <Skeleton className="mt-3 h-4 w-3/4" />
        <div className="mt-9 space-y-7">
          {Array.from({ length: 3 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
            <div key={i}>
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="mt-2.5 h-12 w-full rounded-brand" />
            </div>
          ))}
        </div>
        <Skeleton className="mt-9 h-12 w-40 rounded-brand" />
      </div>
    </LoadingBlock>
  );
}
